import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppContextNonNull } from "@contexts/AppContext";
import moment, { Moment } from "moment-timezone";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import OwButton from "@components/shared/OwButton/OwButton";
import OwBadge from "@components/shared/OwBadge/OwBadge";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import { OwFnTable } from "@components/shared/OwFnTable/OwFnTable";
import PaginationBar from "@components/shared/PaginationBar/PaginationBar";
import OwDateRange from "@components/shared/OwDateRange/OwDateRange";
import UnanetService from "@services/UnanetService";
import IOwTableCols from "@models/IOwTableCols";
import {
    IUnanetHealthResponse,
    IUnanetDashboardStatsResponse,
    IUnanetActivityLogItem,
    IUnanetActivityLogParams,
} from "@models/Unanet/IUnanetDashboardQueryParams";
import { UnanetQueueDetailModal } from "./UnanetQueueDetailModal";
import "./UnanetSyncStatus.scss";
import { Breadcrumb } from "react-bootstrap";
import Breadcrumbs from "@components/shared/Breadcrumbs/Breadcrumbs";

const ACTION_LABELS: Record<string, string> = {
    journal_entry_create: "Journal Entry",
    journal_entry_add_expenses: "JE Expenses Added",
    journal_entry_get: "JE Retrieved",
    connection_test: "Connection Test",
    test_connection_adhoc: "Ad-hoc Test",
    project_search: "Project Search",
    accounts_get: "Accounts Retrieved",
    legal_entities_get: "Legal Entities",
    organizations_get: "Organizations",
};

type StatusFilter = "" | "success" | "failed";
type TypeFilter = "" | "journal_entry" | "connection" | "configuration";

export const UnanetSyncStatus = () => {
    const { userModel, supplierModel } = useAppContextNonNull();

    // Health state
    const [health, setHealth] = useState<IUnanetHealthResponse | null>(null);
    const [healthLoading, setHealthLoading] = useState(true);
    const lastHealthFetch = useRef<number>(0);

    // Stats state
    const [stats, setStats] = useState<IUnanetDashboardStatsResponse | null>(null);

    // Activity log state
    const [activityItems, setActivityItems] = useState<IUnanetActivityLogItem[]>([]);
    const [activityTotal, setActivityTotal] = useState(0);
    const [activityLoading, setActivityLoading] = useState(false);
    const [activityInitialLoad, setActivityInitialLoad] = useState(true);
    const [limit, setLimit] = useState(25);
    const [offset, setOffset] = useState(0);
    const [statusFilter, setStatusFilter] = useState<StatusFilter>("");
    const [typeFilter, setTypeFilter] = useState<TypeFilter>("");
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);

    // Detail modal
    const [selectedSyncLogGuid, setSelectedSyncLogGuid] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // ---------- Data fetching ----------

    const fetchHealth = useCallback(
        async (force?: boolean) => {
            const now = Date.now();
            if (!force && now - lastHealthFetch.current < 5000) return;
            lastHealthFetch.current = now;
            setHealthLoading(true);
            try {
                const data = await UnanetService.getHealth(userModel, supplierModel);
                setHealth(data);
            } catch (e) {
                console.error("Error fetching health:", e);
            } finally {
                setHealthLoading(false);
            }
        },
        [userModel, supplierModel]
    );

    const fetchStats = useCallback(async () => {
        try {
            const data = await UnanetService.getDashboardStats(userModel, supplierModel);
            setStats(data);
        } catch (e) {
            console.error("Error fetching stats:", e);
        }
    }, [userModel, supplierModel]);

    const fetchActivityLog = useCallback(async () => {
        setActivityLoading(true);
        try {
            const params: IUnanetActivityLogParams = {
                limit,
                offset: offset * limit,
            };
            if (statusFilter) params.status = statusFilter;
            if (typeFilter) params.type = typeFilter;
            if (startDate) params.startDate = moment(startDate).format("YYYY-MM-DD");
            if (endDate) params.endDate = moment(endDate).format("YYYY-MM-DD");

            const data = await UnanetService.getActivityLog(params, userModel, supplierModel);
            setActivityItems(data.items || []);
            setActivityTotal(data.totalCount || 0);
        } catch (e) {
            console.error("Error fetching activity log:", e);
        } finally {
            setActivityLoading(false);
            setActivityInitialLoad(false);
        }
    }, [limit, offset, statusFilter, typeFilter, startDate, endDate, userModel, supplierModel]);

    useEffect(() => {
        document.title = "Orderwerks >> Unanet Sync Status";
        fetchHealth(true);
        fetchStats();
    }, [fetchHealth, fetchStats]);

    useEffect(() => {
        fetchActivityLog();
    }, [fetchActivityLog]);

    // ---------- Filter handlers ----------

    const handleStatusFilter = (val: StatusFilter) => {
        setStatusFilter(val);
        setOffset(0);
    };

    const handleTypeFilter = (val: TypeFilter) => {
        setTypeFilter(val);
        setOffset(0);
    };

    const handleStartDate = (date: Moment | null) => {
        setStartDate(date ? date.toDate() : null);
        setOffset(0);
    };

    const handleEndDate = (date: Moment | null) => {
        setEndDate(date ? date.toDate() : null);
        setOffset(0);
    };

    const handlePaginationChanged = (newOffset: number, newLimit: number) => {
        setOffset(newOffset);
        setLimit(newLimit);
    };

    // ---------- Modal handlers ----------

    const openDetailModal = (guid: string) => {
        setSelectedSyncLogGuid(guid);
        setIsModalOpen(true);
    };

    const closeDetailModal = (refresh?: boolean) => {
        setIsModalOpen(false);
        setSelectedSyncLogGuid(null);
        if (refresh) {
            fetchActivityLog();
            fetchHealth(true);
            fetchStats();
        }
    };

    // ---------- Helpers ----------

    const getSuccessRateClass = () => {
        if (!stats) return "";
        if (stats.successRate >= 95) return "UnanetSyncStatus__stats-value--success";
        if (stats.successRate >= 80) return "UnanetSyncStatus__stats-value--warning";
        return "UnanetSyncStatus__stats-value--danger";
    };

    const formatRelativeTime = (dateStr: string | null) => {
        if (!dateStr) return "Never";
        return moment(dateStr).fromNow();
    };

    // ---------- Table columns ----------

    const columns: IOwTableCols[] = useMemo(
        () => [
            {
                title: "timestamp",
                valueProp: "",
                renderFnEmitsRow: true,
                renderFn: (row: IUnanetActivityLogItem) => <span>{moment(row.createdAt).format("M/D/YY, h:mm A")}</span>,
            },
            {
                title: "type",
                valueProp: "",
                renderFnEmitsRow: true,
                renderFn: (row: IUnanetActivityLogItem) => (
                    <OwBadge title={(ACTION_LABELS[row.action] || row.action).toLowerCase().replaceAll(" ", "_")} bgColor="bg-info" />
                ),
            },
            {
                title: "order_reference",
                valueProp: "",
                renderFnEmitsRow: true,
                renderFn: (row: IUnanetActivityLogItem) => {
                    if (row.orderGuid && row.orderName) {
                        return <a href={`/orders/${row.orderGuid}/entry`}>{row.orderName}</a>;
                    }
                    return <span className="text-muted">&mdash;</span>;
                },
            },
            {
                title: "status",
                valueProp: "",
                className: "text-center",
                renderFnEmitsRow: true,
                renderFn: (row: IUnanetActivityLogItem) => (
                    <OwBadge
                        title={row.status}
                        bgColor={row.status === "success" ? "bg-success" : "bg-danger"}
                        leftIconClass={row.status === "success" ? "fas fa-check" : "fas fa-times"}
                    />
                ),
            },
            {
                title: "details",
                valueProp: "",
                renderFnEmitsRow: true,
                renderFn: (row: IUnanetActivityLogItem) => {
                    if (!row.errorMessage) return <span className="text-muted">—</span>;
                    const truncated = row.errorMessage.length > 60 ? row.errorMessage.substring(0, 60) + "..." : row.errorMessage;
                    return (
                        <span className="UnanetSyncStatus__error-text" title={row.errorMessage}>
                            {truncated}
                        </span>
                    );
                },
            },
            {
                title: "actions",
                valueProp: "",
                isActionsCol: true,
                className: "text-end",
                renderFnEmitsRow: true,
                renderFn: (row: IUnanetActivityLogItem) => (
                    <OwButton
                        title={"details"}
                        className={"btn-sm btn-outline-secondary"}
                        leftIconClass={"fas fa-chevron-down"}
                        fn={() => openDetailModal(row.guid)}
                    />
                ),
            },
        ],
        []
    );

    // ---------- Render ----------

    return (
        <div className="UnanetSyncStatus">
            <Breadcrumbs
                crumbs={[
                    { title: "business_integrations", url: "/business/integrations" },
                    { title: "unanet_sync_status", url: "/business/integrations/unanet/sync-status" },
                ]}
            />

            <OwTitle title="unanet_sync_status" leftIconName="fas fa-sync-alt" />

            {/* Section A: Health Overview */}
            <div className="UnanetSyncStatus__health-row">
                {/* Connection */}
                <div className="UnanetSyncStatus__health-card">
                    <div className="UnanetSyncStatus__health-label">
                        <OwLabel title="connection_status" noBottomMargin skipTranslate={false} />
                        <i className={`fas fa-plug ${health?.connection.isConnected ? "text-success" : "text-danger"}`} />
                    </div>
                    <div className="UnanetSyncStatus__health-value">
                        {health?.connection.isConnected ? (
                            <>
                                <OwLabel title="connected" noBottomMargin skipTranslate={false} />
                                <i className="fas fa-check-circle text-success" />
                            </>
                        ) : (
                            <>
                                <OwLabel title="disconnected" noBottomMargin skipTranslate={false} />
                                <i className="fas fa-times-circle text-danger" />
                            </>
                        )}
                    </div>
                    {health?.connection.error && <small className="text-danger">{health.connection.error}</small>}
                </div>

                {/* Last Project Sync */}
                <div className="UnanetSyncStatus__health-card">
                    <div className="UnanetSyncStatus__health-label">
                        <OwLabel title="last_project_sync" noBottomMargin skipTranslate={false} />
                        <i className="fas fa-sync-alt text-info" />
                    </div>
                    <div className="UnanetSyncStatus__health-value">{formatRelativeTime(health?.lastSuccessfulSync ?? null)}</div>
                    <div>
                        <OwButton
                            title="refresh"
                            className="btn-sm btn-outline-secondary mt-1"
                            leftIconClass="fas fa-redo"
                            active={healthLoading}
                            disableOnActive={true}
                            fn={() => fetchHealth(true)}
                        />
                    </div>
                </div>

                {/* Pending */}
                <div className="UnanetSyncStatus__health-card">
                    <div className="UnanetSyncStatus__health-label">
                        <OwLabel title="pending" noBottomMargin skipTranslate={false} />
                        <i className="fas fa-clock text-warning" />
                    </div>
                    <div className="UnanetSyncStatus__health-value">{health?.queue?.pending ?? 0}</div>
                </div>

                {/* Failed */}
                <div
                    className={`UnanetSyncStatus__health-card ${(health?.queue?.failed ?? 0) > 0 ? "UnanetSyncStatus__health-card--danger" : ""}`}>
                    <div className="UnanetSyncStatus__health-label">
                        <OwLabel title="failed_needs_attention" noBottomMargin skipTranslate={false} />
                        {(health?.queue?.failed ?? 0) > 0 && <i className="fas fa-exclamation-triangle text-danger" />}
                    </div>
                    <div className="UnanetSyncStatus__health-value">{health?.queue?.failed ?? 0}</div>
                </div>
            </div>

            {/* Section B: Quick Stats Cards */}
            <div className="UnanetSyncStatus__stats-row">
                <div className="UnanetSyncStatus__stats-card">
                    <div className="UnanetSyncStatus__stats-icon">
                        <i className="fas fa-calendar-day" />
                    </div>
                    <div className="UnanetSyncStatus__stats-value">{stats?.syncedToday ?? 0}</div>
                    <div className="UnanetSyncStatus__stats-label">
                        <OwLabel title="orders_synced_today" noBottomMargin />
                    </div>
                </div>

                <div className="UnanetSyncStatus__stats-card">
                    <div className="UnanetSyncStatus__stats-icon">
                        <i className="fas fa-calendar-week" />
                    </div>
                    <div className="UnanetSyncStatus__stats-value">{stats?.syncedThisWeek ?? 0}</div>
                    <div className="UnanetSyncStatus__stats-label">
                        <OwLabel title="orders_synced_this_week" noBottomMargin />
                    </div>
                </div>

                <div className="UnanetSyncStatus__stats-card">
                    <div className="UnanetSyncStatus__stats-icon">
                        <i className="fas fa-chart-line" />
                    </div>
                    <div className={`UnanetSyncStatus__stats-value ${getSuccessRateClass()}`}>
                        {stats?.successRate != null ? `${stats.successRate}%` : "—"}
                    </div>
                    <div className="UnanetSyncStatus__stats-label">
                        <OwLabel title="success_rate" noBottomMargin />
                    </div>
                </div>
            </div>

            {/* Section C: Activity Log */}
            <div className="UnanetSyncStatus__activity-section">
                <div className="UnanetSyncStatus__activity-header">
                    <i className="fas fa-list-alt" />
                    <OwLabel title="recent_activity" noBottomMargin />
                </div>

                {/* Filters */}
                <div className="UnanetSyncStatus__activity-filters">
                    {/* Status filter buttons */}
                    <div className="UnanetSyncStatus__filter-group">
                        <OwButton
                            title="all"
                            className={`btn-sm ${statusFilter === "" ? "btn-primary" : "btn-outline-secondary"}`}
                            fn={() => handleStatusFilter("")}
                        />
                        <OwButton
                            title="success"
                            className={`btn-sm ${statusFilter === "success" ? "btn-success" : "btn-outline-secondary"}`}
                            fn={() => handleStatusFilter("success")}
                        />
                        <OwButton
                            title="failed"
                            className={`btn-sm ${statusFilter === "failed" ? "btn-danger" : "btn-outline-secondary"}`}
                            fn={() => handleStatusFilter("failed")}
                        />
                    </div>

                    <div className="UnanetSyncStatus__filter-separator" />

                    {/* Type filter buttons */}
                    <div className="UnanetSyncStatus__filter-group">
                        <OwButton
                            title="all_types"
                            className={`btn-sm ${typeFilter === "" ? "btn-primary" : "btn-outline-secondary"}`}
                            fn={() => handleTypeFilter("")}
                        />
                        <OwButton
                            title="journal_entries"
                            className={`btn-sm ${typeFilter === "journal_entry" ? "btn-primary" : "btn-outline-secondary"}`}
                            fn={() => handleTypeFilter("journal_entry")}
                        />
                        <OwButton
                            title="connection"
                            className={`btn-sm ${typeFilter === "connection" ? "btn-primary" : "btn-outline-secondary"}`}
                            fn={() => handleTypeFilter("connection")}
                        />
                        <OwButton
                            title="configuration"
                            className={`btn-sm ${typeFilter === "configuration" ? "btn-primary" : "btn-outline-secondary"}`}
                            fn={() => handleTypeFilter("configuration")}
                        />
                    </div>

                    <div className="UnanetSyncStatus__filter-separator" />

                    {/* Date range filter */}
                    <div className="UnanetSyncStatus__date-filter">
                        <OwDateRange
                            startDateSelected={startDate}
                            endDateSelected={endDate}
                            startFn={handleStartDate}
                            endFn={handleEndDate}
                            startLabel="start_date"
                            endLabel="end_date"
                            startLabelClassName="me-1 w-7r"
                            endLabelClassName="me-1 w-7r"
                        />
                    </div>
                </div>

                {/* Activity table */}
                <OwFnTable
                    columns={columns}
                    dataSet={activityItems}
                    dataFetch={activityLoading}
                    initialData={activityInitialLoad}
                    tableConfig={{
                        tableHover: true,
                        tableStriped: true,
                    }}
                />

                {/* Pagination */}
                <PaginationBar limit={limit} offset={offset} totalRecords={activityTotal} onPaginationChanged={handlePaginationChanged} />
            </div>

            {/* Detail Modal */}
            {selectedSyncLogGuid && (
                <UnanetQueueDetailModal syncLogGuid={selectedSyncLogGuid} isOpen={isModalOpen} onClose={closeDetailModal} />
            )}
        </div>
    );
};
